import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, ArrowRight, BookMarked, MessageSquareQuote,
  PanelRightClose, RefreshCw, ScrollText, Square, Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { numa, theses as thesesApi } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import { toast } from "sonner";
import NumaLogo from "@/components/NumaLogo";
import { unit } from "@/lib/projectTypes";
import { sanitizeHtml } from "@/lib/sanitize";
import AiBadge from "@/components/AiBadge";

/**
 * Editorial actions. The wording is deliberately peer-review, not viva:
 * the same operations serve a student and a professor, but only one of them
 * is preparing for a graduation committee.
 */
/**
 * The readings NUMA can give on a section.
 *
 * `short` is what fits on one line of buttons; `title` is the full name, used
 * where there is room for it - the heading of the result window.
 */
export const ACTIONS = [
  {
    key: "review",
    title: "Revisione tra pari",
    short: "Revisione",
    desc: "Rilievi critici su argomentazione, evidenze e chiarezza",
    icon: ScrollText,
  },
  {
    key: "objections",
    title: "Obiezioni di un referee",
    short: "Obiezioni",
    desc: "Dove il testo è aggredibile, e come cautelarsi",
    icon: MessageSquareQuote,
  },
  {
    key: "problems",
    title: "Problemi e riscrittura",
    short: "Problemi",
    desc: "Incongruenze e ripetizioni, con versione corretta",
    icon: AlertTriangle,
  },
  {
    key: "improve",
    title: "Affina la prosa",
    short: "Prosa",
    desc: "Più preciso e scorrevole, contenuto invariato",
    icon: Wand2,
  },
  {
    key: "terminology",
    title: "Coerenza terminologica",
    short: "Termini",
    desc: "Termini, sigle e notazione usati in modo uniforme",
    icon: BookMarked,
  },
  {
    key: "venue_fit",
    title: "Adeguatezza alla sede",
    short: "Sede",
    desc: "Confronto con le convenzioni della rivista o collana",
    icon: ArrowRight,
    needsVenue: true,
  },
];

function stripHtml(html) {
  const d = document.createElement("div");
  d.innerHTML = sanitizeHtml(html);
  return d.innerText;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function textToHtml(text = "") {
  return String(text)
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br />")}</p>`)
    .join("\n");
}

function extractImprovedText(action, result = "") {
  const clean = result.trim();
  if (action === "problems") {
    const match = /VERSIONE MIGLIORATA COMPLETA\s*:?/i.exec(clean);
    if (match) {
      const improved = clean.slice(match.index + match[0].length).trim();
      if (improved) return improved;
    }
  }
  return clean;
}

function ResultBody({ action, text }) {
  if (action === "objections") {
    const items = text.split(/\n+/).filter((l) => /^\s*\d+[.)]/.test(l));
    if (items.length) {
      return (
        <ol className="space-y-3">
          {items.map((q, i) => (
            <li key={i} className="numa-panel p-3.5">
              <p className="numa-eyebrow mb-1">Obiezione {i + 1}</p>
              <p className="text-[14px] leading-relaxed text-ink">{q.replace(/^\s*\d+[.)]\s*/, "")}</p>
            </li>
          ))}
        </ol>
      );
    }
  }
  return (
    <div className="whitespace-pre-wrap font-serif-display text-[15px] leading-relaxed text-ink">{text}</div>
  );
}

/**
 * The readings themselves, and the window their results arrive in.
 *
 * The rail this used to live in is gone: the same actions now sit in the
 * review and in the editor, where they are seen. What remains is the machinery
 * - running the action, streaming the answer, applying a rewrite - mounted
 * once and triggered from either place, so there is one implementation rather
 * than two that drift apart.
 */
// Sotto questa soglia una parte è un titolo, non un testo da rileggere.
const MINIMO_LEGGIBILE = 200;

export default function NumaPanel({ chapter, thesis, onChapterUpdate, request }) {
  const { ensureKey, reportError } = useLLM();
  const [open, setOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [venue, setVenue] = useState("");
  const abortRef = useRef(null);

  const unitLabel = useMemo(() => unit(thesis?.project_type), [thesis?.project_type]);
  const activeMeta = ACTIONS.find((a) => a.key === activeAction);

  /*
    Quale parte legge NUMA.

    Da quando un documento importato si taglia a ogni titolo, la parte aperta
    può essere un titolo di paragrafo con tre righe sotto - e chiedere una
    rilettura rispondeva «serve almeno un paragrafo di testo», che è vero e
    inutile. Si legge allora la prima parte del documento che abbia testo a
    sufficienza, e si dice a schermo quale: meglio leggere qualcosa dicendo che
    cosa, che rifiutarsi.
  */
  const abbastanzaTesto = (c) => stripHtml(c?.content || "").trim().length >= MINIMO_LEGGIBILE;
  const daLeggere = useMemo(() => {
    if (abbastanzaTesto(chapter)) return chapter;
    return (thesis?.chapters || []).find(abbastanzaTesto) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter, thesis?.chapters]);
  const sostituita = Boolean(daLeggere && chapter && daLeggere.id !== chapter.id);

  // An action started from somewhere else on the page - the review, where
  // these readings matter most and where the right-hand rail is easy to miss.
  useEffect(() => {
    if (request?.key) run(request.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.at]);

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
  };

  const applyImprovedResult = async () => {
    if (!thesis?.thesis_id || !daLeggere?.id) return;
    const improvedText = extractImprovedText(activeAction, result);
    if (!improvedText) {
      toast.error("Non c'è una versione riscritta da applicare.");
      return;
    }
    setApplying(true);
    try {
      const res = await thesesApi.saveImprovement(thesis.thesis_id, daLeggere.id, {
        improved_text: textToHtml(improvedText),
 apply: true,
      });
      const updated = res.thesis || (await thesesApi.get(thesis.thesis_id));
      onChapterUpdate?.(updated);
      setOpen(false);
      toast.success("Versione applicata. La precedente resta in Versioni.");
    } catch (err) {
      reportError(err);
    } finally {
      setApplying(false);
    }
  };

  const run = async (actionKey) => {
    if (!thesis?.chapters?.length) {
      toast.error(`Seleziona prima un${unitLabel === "Capitolo" ? " capitolo" : "a sezione"}.`);
      return;
    }
    if (!daLeggere) {
      toast.error("Non c'è ancora abbastanza testo da rileggere.", {
        description: "Scrivi o importa qualche paragrafo, poi torna qui.",
      });
      return;
    }
    const text = stripHtml(daLeggere.content || "");
    // The key is requested here, at the first action that needs a model.
    if (!(await ensureKey())) return;

    setActiveAction(actionKey);
    setOpen(true);
    setResult("");
    setLoading(true);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    await numa.stream({
 action: actionKey,
      text,
      chapter_title: daLeggere.title,
      project_type: thesis?.project_type,
 venue: actionKey === "venue_fit" ? venue : undefined,
 signal: ctrl.signal,
      onDelta: (chunk) => setResult((r) => r + chunk),
      onError: (err) => {
        const normalized = reportError(err);
        setOpen(false);
        toast.error(normalized.message, { description: normalized.action || undefined });
      },
      onDone: () => setLoading(false),
    });
    if (abortRef.current === ctrl) abortRef.current = null;
    setLoading(false);
  };

  const canApply = !loading && result && (activeAction === "improve" || activeAction === "problems");

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="numa-result-dialog" className="max-w-[95vw] sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif-display text-lg">
              <NumaLogo size={6} className="numa-mark" variant={3} />
              {activeMeta?.title}
            </DialogTitle>
            <DialogDescription className="numa-meta">
              {daLeggere?.title} · generato da NUMA, da valutare criticamente
              {sostituita && (
                <span className="mt-1 block">
                  La parte aperta, «{chapter?.title}», ha poco testo: NUMA ha letto questa.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[58vh] pr-4">
            {loading && !result && (
              <div className="space-y-2.5 py-3" aria-live="polite">
                <div className="numa-shimmer h-3.5 w-full rounded" />
                <div className="numa-shimmer h-3.5 w-5/6 rounded" />
                <div className="numa-shimmer h-3.5 w-4/6 rounded" />
                <span className="sr-only">NUMA sta elaborando la revisione</span>
              </div>
            )}
            {result && (
              <div className="relative">
                <ResultBody action={activeAction} text={result} />
                {loading && (
                  <span
                    data-testid="streaming-cursor"
                    className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-assistant align-middle"
                  />
                )}
              </div>
            )}
          </ScrollArea>

          {canApply && (
            <div className="numa-panel-assistant p-3.5">
              <p className="text-[13px] font-medium text-ink">Sostituire il testo con la versione riscritta?</p>
              <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-muted">
                La versione attuale resta recuperabile da Versioni.
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={applying}>
                  Tieni l'originale
                </Button>
                <Button
                  size="sm"
                  data-testid="numa-apply-improved-btn"
                  onClick={applyImprovedResult}
                  disabled={applying}
                >
                  {applying ? "Applicazione…" : "Applica"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-between border-t border-line pt-3">
            {loading ? (
              <Button data-testid="numa-stop-btn" variant="outline" size="sm" onClick={stop}>
                <Square className="h-3.5 w-3.5" /> Interrompi
              </Button>
            ) : (
              <Button
                data-testid="numa-regenerate-btn"
                variant="outline"
                size="sm"
                onClick={() => run(activeAction)}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Rigenera
              </Button>
            )}
            <Button size="sm" data-testid="numa-close-btn" onClick={() => { stop(); setOpen(false); }}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
